import { isEqual, mapKeys } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCustomEventEmitter } from '@/hook';
import { createSafeContext, deepCopy, uuid } from '@/util';

import { VTREE_TRIGGER } from './constant';
import type { VTreeContextValue, VTreeNode, VTreeProps } from './type';
import { getVTreeNodeList as _getVTreeNodeList } from './util';

export const VTreeContext = createSafeContext<VTreeContextValue>();

const listify = <T,>(value: T | T[]) =>
  value instanceof Array ? value : [value];

export const VTreeContextProvider = ({
  children,
  autoOpen = true,
  ...rest
}: React.PropsWithChildren<VTreeProps>) => {
  const [expandedIdList, _setExpandedIdList] = useState(
    [] as VTreeNode['id'][],
  );
  const expandedIdMap = useMemo(
    () =>
      expandedIdList.reduce(
        (acc, id) => ({ ...acc, [id]: true }),
        {} as { [id: VTreeNode['id']]: boolean },
      ),
    [expandedIdList],
  );
  const [_nodeList, _setNodeList] = useState(rest.nodeList);
  const [selectedIdList, _setSelectedIdList] = useState(
    [] as VTreeNode['id'][],
  );
  const selectedIdMap = useMemo(
    () =>
      selectedIdList.reduce(
        (acc, id) => ({ ...acc, [id]: true }),
        {} as { [id: VTreeNode['id']]: boolean },
      ),
    [selectedIdList],
  );

  const [nodeList, nodeById] = useMemo(() => {
    const nodeById = {} as Dictionary<VTreeNode>;

    const assignId = (
      node: VTreeNode,
      parentIdList: string[] = [],
    ): VTreeNode => {
      const id = node.id ?? uuid();

      nodeById[id] = {
        ...node,
        children: node.children?.map(node =>
          assignId(node, parentIdList.concat(id)),
        ),
        get id() {
          return id;
        },
      } as VTreeNode;

      return nodeById[id];
    };

    return [_nodeList.map(node => assignId(node as VTreeNode, [])), nodeById];
  }, [_nodeList]);

  const { eventEmitter } = useCustomEventEmitter({
    onClose: rest.onClose ?? (() => {}),
    onOpen: rest.onOpen ?? (() => {}),
    onSelect: rest.onSelect ?? (() => {}),
  });

  const closeNode: VTreeContextValue['closeNode'] = node =>
    setExpandedIdList(
      listify(node).map(n => n.id),
      false,
    );

  const deselectAllNode: VTreeContextValue['deselectAllNode'] = () => {
    setSelectedIdList([]);
  };

  const deselectNode: VTreeContextValue['deselectNode'] = node => {
    const nodeById = mapKeys(listify(node), node => node.label);
    const newNodeList = selectedIdList.filter(id => !nodeById[id]);

    setSelectedIdList(newNodeList);
  };

  const emit: VTreeContextValue['emit'] = (name, target, trigger) =>
    eventEmitter.current.emit(name, target, {
      trigger: trigger ?? VTREE_TRIGGER.REF,
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getVTreeNodeList = useCallback(_getVTreeNodeList(nodeById), [nodeById]);

  const isExpandedNode: VTreeContextValue['isExpandedNode'] = useCallback(
    node => expandedIdMap[node.id],
    [expandedIdMap],
  );

  const isSelectedNode: VTreeContextValue['isSelectedNode'] = useCallback(
    node => selectedIdMap[node.id],
    [selectedIdMap],
  );

  const getNodeStatus: VTreeContextValue['getNodeStatus'] = useCallback(
    node => {
      if (node == null) {
        console.warn(new Error('`node` do not nullable'));

        return { disabled: false, expanded: false, selected: false };
      }
      const disabled = node.disabled ?? false;
      const expanded = isExpandedNode(node);
      const selected = isSelectedNode(node);

      return { disabled, expanded, selected };
    },
    [isExpandedNode, isSelectedNode],
  );

  const openNode: VTreeContextValue['openNode'] = node =>
    setExpandedIdList(
      listify(node).map(n => n.id),
      true,
    );

  const selectAllNode: VTreeContextValue['selectAllNode'] = () =>
    selectNode(nodeList);

  const selectNode: VTreeContextValue['selectNode'] = node => {
    const idList = listify(node)
      .map(node => node.id)
      .toSorted();

    if (!isEqual(selectedIdList.toSorted(), idList)) {
      return setSelectedIdList([...idList]);
    }
  };

  const setChildren: VTreeContextValue['setChildren'] = (
    nodeOrNodeId,
    children,
  ) => {
    const id =
      typeof nodeOrNodeId === 'string' ? nodeOrNodeId : nodeOrNodeId.id;
    const node = nodeById[id];

    if (node == null) {
      console.warn(`Not found Node(${id})`);

      return;
    }

    node.children = children;

    _setNodeList([...nodeList]);
  };

  const setExpandedIdList = (
    expandedIdList: VTreeNode['id'][],
    expanded: boolean,
  ) => {
    const clonedIdMap = deepCopy(expandedIdMap);

    expanded
      ? expandedIdList.forEach(id => (clonedIdMap[id] = true))
      : expandedIdList.forEach(id => delete clonedIdMap[id]);

    const newExpandedIdList = Object.keys(clonedIdMap);

    _setExpandedIdList(newExpandedIdList);

    return newExpandedIdList;
  };

  const setSelectedIdList = (selectedIdList: VTreeNode['id'][]) => {
    _setSelectedIdList(selectedIdList);

    return selectedIdList;
  };

  const onSelectNode: VTreeContextValue['onSelectNode'] = node => {
    const status = getNodeStatus(node);

    if (status?.disabled) return;

    if (!status.selected) {
      const selectedIdList = selectNode(node);

      if (selectedIdList) {
        emit(
          'select',
          { result: getVTreeNodeList(selectedIdList) },
          VTREE_TRIGGER.CLICK,
        );

        autoOpen && !status.expanded && onToggleIcon(node);
      }
    }
  };

  const onToggleIcon: VTreeContextValue['onToggleIcon'] = node => {
    const status = getNodeStatus(node);

    if (status?.disabled) return;

    const expanded = status.expanded;

    !expanded ? openNode(node) : closeNode(node);

    emit(
      !expanded ? 'open' : 'close',
      { result: getVTreeNodeList([node.id]) },
      VTREE_TRIGGER.CLICK,
    );
  };

  useEffect(() => {
    _setNodeList(rest.nodeList);
  }, [rest.nodeList]);

  return (
    <VTreeContext.Provider<VTreeContextValue>
      value={{
        ...rest,
        autoOpen,
        nodeById,
        nodeList,
        selectedIdList,
        eventEmitter: eventEmitter.current,

        closeNode,
        deselectAllNode,
        deselectNode,
        emit,
        getNodeStatus,
        isExpandedNode,
        isSelectedNode,
        openNode,
        selectAllNode,
        selectNode,
        setChildren,

        onSelectNode,
        onToggleIcon,
      }}
    >
      {children}
    </VTreeContext.Provider>
  );
};
